import React, { useContext } from 'react';
import { LocaleContext } from '../../../contexts/LocaleContext';
import Button from '../../button/Button';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';

import 'iconify-icon';
import { useLocalizedNavigation } from '@repo/gatsby-localization-helpers';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'iconify-icon': {
        icon: string;
        flip?: string;
        rotate?: string | number;
        width?: string | number;
        height?: string | number;
        style?: React.CSSProperties;
        class?: string;
      };
    }
  }
}

interface BlockBuilderImageWithHorizontalTextListProps {
  value: {
    headline?: string;
    copy?: string;
    items: any[];
    cta: any;
  };
  getInternalLink: any;
}

function BlockBuilderImageWithHorizontalTextList({
  value,
  getInternalLink,
}: Readonly<BlockBuilderImageWithHorizontalTextListProps>) {
  const localeContext = useContext(LocaleContext);
  const currency = (localeContext as any)?.currency || 'USD';
  const { headline, copy, items, cta } = value;
  const navigate = useLocalizedNavigation();

  const handleCtaClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (cta?.linkType === 'external') {
      if (cta?.blank) {
        window.open(cta.href, '_blank');
      } else {
        window.location = cta.href;
      }
    } else {
      navigate(
        getInternalLink({
          slug: `/${cta?.internalLink?.slug?.current || ''}`,
          currency,
        })
      );
    }
  };

  return (
    <div id="BlockBuilderImageWithHorizontalTextList">
      {headline && <h2 className="whitespace-break-spaces mb-4">{headline}</h2>}
      {copy && <p className="mb-4">{copy}</p>}
      <div className="flex flex-col gap-4 max-w-4xl md:mx-auto">
        {items?.map(item => (
          <div key={item._key} className="flex gap-4">
            <div className="flex items-center justify-center">
              {item?.mediaType === 'picker' && item?.icon?.icon && (
                <iconify-icon
                  icon={item.icon.icon}
                  flip={item.icon.metadata.flip}
                  rotate={item.icon.metadata.rotate}
                  width={item.icon?.metadata?.size?.width}
                  height={item.icon?.metadata?.size?.height}
                  style={{
                    color: item.icon?.metadata?.color?.hex,
                    display: 'inline-block',
                    width: item.icon?.metadata?.size?.width || '64px',
                    height: item.icon?.metadata?.size?.height || '64px',
                  }}
                  class={
                    item.icon?.metadata?.color?.hex
                      ? ''
                      : 'text-foreground-headline'
                  }
                />
              )}
              {item?.mediaType === 'upload' && item?.media?.asset?.url && (
                <img
                  src={item.media.asset.url}
                  width="64"
                  height="64"
                  className="max-w-fit"
                  alt={item?.media?.alt || ''}
                />
              )}
            </div>
            <div className="flex flex-col self-center">
              {item?.headline && (
                <p className="m-0 text-foreground-headline text-lg font-bold">
                  {item.headline}
                </p>
              )}
              {item?.copy && (
                <div className="w-full text-base font-normal [&>p]:mb-0">
                  <BlockBuilder
                    blocks={item.copy}
                    getInternalLink={getInternalLink}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        {cta?.linkType !== 'disable' && cta?.caption && (
          <div className="mt-4">
            <Button onClick={handleCtaClick} className="!w-full md:!w-auto">
              {cta.caption}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BlockBuilderImageWithHorizontalTextList;
